/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */

import { cssMap, jsx } from '@compiled/react';

import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	tableWrapper: {
		boxSizing: 'border-box',
		width: '100%',
		flexShrink: 0,
		maxHeight: `calc(100% - ${token('space.300')})`,
	},
	table: {
		display: 'table',
		tableLayout: 'fixed',
		width: '100%',
		borderCollapse: 'separate',
		borderSpacing: '0px',
		borderWidth: '0px',
	},
	thead: {
		display: 'table-header-group',
	},
	headerRow: {
		display: 'table-row',
		height: '40px',
	},
	thFixed: {
		display: 'table-cell',
		verticalAlign: 'middle',
		width: '456px',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		// Border must be on th cells, not thead — table-header-group doesn't support border
		// Use color.skeleton so the line blends with the shimmer elements
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.skeleton'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
		borderRightWidth: '0px',
	},
	thBase: {
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		// Border must be on th cells, not thead — table-header-group doesn't support border
		// Use color.skeleton so the line blends with the shimmer elements
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.skeleton'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
		borderRightWidth: '0px',
	},
	tbody: {
		display: 'table-row-group',
	},
	bodyRow: {
		display: 'table-row',
		height: '40px',
	},
	tdBase: {
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		// No borders between body cells or rows
		borderWidth: '0px',
	},
	cellContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

// Varying text widths so rows look natural, not uniform
const WORK_TEXT_WIDTHS = ['240px', '320px', '280px', '360px', '200px', '300px', '260px', '340px', '220px', '290px'];

const SkeletonHeaderRow = () => (
	<tr css={styles.headerRow}>
		{/* Work column */}
		<th css={[styles.thBase, styles.thFixed]}>
			<Skeleton width="60px" height="12px" borderRadius={token('radius.full')} isShimmering />
		</th>
		{/* Status */}
		<th css={styles.thBase}>
			<Skeleton width="48px" height="12px" borderRadius={token('radius.full')} isShimmering />
		</th>
		{/* Assignee */}
		<th css={styles.thBase}>
			<Skeleton width="56px" height="12px" borderRadius={token('radius.full')} isShimmering />
		</th>
		{/* Labels */}
		<th css={styles.thBase}>
			<Skeleton width="44px" height="12px" borderRadius={token('radius.full')} isShimmering />
		</th>
		{/* Priority */}
		<th css={styles.thBase}>
			<Skeleton width="48px" height="12px" borderRadius={token('radius.full')} isShimmering />
		</th>
	</tr>
);

const SkeletonBodyRow = ({ textWidth }: { textWidth: string }) => (
	<tr css={styles.bodyRow}>
		{/* Work column: small square icon + text bar */}
		<td css={styles.tdBase}>
			<div css={styles.cellContent}>
				<Skeleton width="16px" height="16px" borderRadius={token('radius.small')} isShimmering />
				<Skeleton width={textWidth} height="12px" borderRadius={token('radius.full')} isShimmering />
			</div>
		</td>
		{/* Status: short pill */}
		<td css={styles.tdBase}>
			<Skeleton width="60px" height="20px" borderRadius={token('radius.full')} isShimmering />
		</td>
		{/* Assignee: circle avatar + short text */}
		<td css={styles.tdBase}>
			<div css={styles.cellContent}>
				<Skeleton width="16px" height="16px" borderRadius={token('radius.full')} isShimmering />
				<Skeleton width="64px" height="12px" borderRadius={token('radius.full')} isShimmering />
			</div>
		</td>
		{/* Labels: circle + short text */}
		<td css={styles.tdBase}>
			<div css={styles.cellContent}>
				<Skeleton width="16px" height="16px" borderRadius={token('radius.full')} isShimmering />
				<Skeleton width="72px" height="12px" borderRadius={token('radius.full')} isShimmering />
			</div>
		</td>
		{/* Priority: small square icon only */}
		<td css={styles.tdBase}>
			<Skeleton width="16px" height="16px" borderRadius={token('radius.small')} isShimmering />
		</td>
	</tr>
);

type SearchResultsLiteTableSkeletonProps = {
	/** Number of skeleton rows to render. Defaults to 10. */
	rowCount?: number;
};

export const SearchResultsLiteTableSkeleton = ({
	rowCount = 10,
}: SearchResultsLiteTableSkeletonProps): JSX.Element => (
	<div css={styles.tableWrapper}>
		<table css={styles.table} data-testid="search-results-lite-table-skeleton">
			<thead css={styles.thead}>
				<SkeletonHeaderRow />
			</thead>
			<tbody css={styles.tbody}>
				{Array.from({ length: rowCount }).map((_, index) => (
					<SkeletonBodyRow
						key={index}
						textWidth={WORK_TEXT_WIDTHS[index % WORK_TEXT_WIDTHS.length]}
					/>
				))}
			</tbody>
		</table>
	</div>
);
